import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { entryAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Building2, Download, FileText, Loader2, RefreshCw, MessageCircle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function BlockReportPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [aggregateData, setAggregateData] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState({
    class_name: '',
    subject: '',
    exam: '',
    year: ''
  });

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await entryAPI.getAll();
      const data = response.data || [];
      setEntries(data);
      
      if (data.length > 0) {
        const first = data[0];
        setSelectedConfig({
          class_name: first.class_name,
          subject: first.subject,
          exam: first.exam,
          year: first.year
        });
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Calculate block aggregate when config changes
  useEffect(() => {
    if (!selectedConfig.class_name) return;
    
    const matchingEntries = entries.filter(e => 
      e.class_name === selectedConfig.class_name &&
      e.subject === selectedConfig.subject &&
      e.exam === selectedConfig.exam &&
      e.year === selectedConfig.year
    );

    if (matchingEntries.length === 0) {
      setAggregateData(null);
      return;
    }

    const allBlueprints = [];
    const allStudents = [];
    const clusterData = {};
    
    matchingEntries.forEach(entry => {
      const bp = JSON.parse(entry.items || '[]');
      const stu = JSON.parse(entry.students || '[]');
      const cluster = entry.cluster || user?.cluster || 'Unknown';
      
      if (allBlueprints.length === 0) {
        allBlueprints.push(...bp);
      }
      
      if (!clusterData[cluster]) {
        clusterData[cluster] = { students: [], schools: 0 };
      }
      clusterData[cluster].schools++;
      
      stu.forEach(s => {
        allStudents.push({ ...s, cluster });
        clusterData[cluster].students.push(s);
      });
    });

    // Group by LO
    const loMap = {};
    const loOrder = [];
    
    allBlueprints.forEach(item => {
      if (!loMap[item.loCode]) {
        loMap[item.loCode] = { items: [], totalMarks: 0 };
        loOrder.push(item.loCode);
      }
      loMap[item.loCode].items.push(item);
      loMap[item.loCode].totalMarks += item.marks || 0;
    });

    const loGroups = loOrder.map(lo => ({
      lo,
      items: loMap[lo].items,
      totalMarks: loMap[lo].totalMarks
    }));

    // Calculate totals per LO
    const loTotals = {};
    loGroups.forEach(group => {
      let total = 0;
      let maxPossible = group.totalMarks * allStudents.length;
      
      allStudents.forEach(student => {
        if (student.marks) {
          group.items.forEach(item => {
            const mark = student.marks[item.id];
            if (typeof mark === 'number') {
              total += mark;
            }
          });
        }
      });
      
      loTotals[group.lo] = {
        total,
        maxPossible,
        percentage: maxPossible > 0 ? ((total / maxPossible) * 100).toFixed(1) : 0
      };
    });

    // Calculate per-cluster totals
    const clusterTotals = {};
    Object.entries(clusterData).forEach(([cluster, data]) => {
      clusterTotals[cluster] = {
        students: data.students.length,
        schools: data.schools,
        loTotals: {}
      };
      
      loGroups.forEach(group => {
        let total = 0;
        let maxPossible = group.totalMarks * data.students.length;
        
        data.students.forEach(student => {
          if (student.marks) {
            group.items.forEach(item => {
              const mark = student.marks[item.id];
              if (typeof mark === 'number') {
                total += mark;
              }
            });
          }
        });
        
        clusterTotals[cluster].loTotals[group.lo] = {
          total,
          maxPossible,
          percentage: maxPossible > 0 ? ((total / maxPossible) * 100).toFixed(1) : 0
        };
      });
    });

    setAggregateData({
      block: user?.block || 'Unknown Block',
      district: user?.district || 'Unknown District',
      config: selectedConfig,
      loGroups,
      loTotals,
      clusterTotals,
      clusters: Object.keys(clusterData),
      totalStudents: allStudents.length,
      totalSchools: matchingEntries.length
    });
  }, [entries, selectedConfig, user]);

  const uniqueConfigs = entries.reduce((acc, entry) => {
    const key = `${entry.class_name}-${entry.subject}-${entry.exam}-${entry.year}`;
    if (!acc.find(c => c.key === key)) {
      acc.push({
        key,
        class_name: entry.class_name,
        subject: entry.subject,
        exam: entry.exam,
        year: entry.year
      });
    }
    return acc;
  }, []);

  const handleExportPDF = () => {
    if (!aggregateData) return;

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('BLOCK AGGREGATE REPORT', doc.internal.pageSize.width / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Block: ${aggregateData.block} | District: ${aggregateData.district}`, doc.internal.pageSize.width / 2, 22, { align: 'center' });
    doc.text(`Class: ${aggregateData.config.class_name} | Subject: ${aggregateData.config.subject} | Exam: ${aggregateData.config.exam} | Year: ${aggregateData.config.year}`, doc.internal.pageSize.width / 2, 28, { align: 'center' });
    doc.text(`Total Clusters: ${aggregateData.clusters.length} | Total Schools: ${aggregateData.totalSchools} | Total Students: ${aggregateData.totalStudents}`, doc.internal.pageSize.width / 2, 34, { align: 'center' });

    // Block summary table
    const headers = [['LO Code', 'Max Marks', 'Total Obtained', 'Max Possible', 'Achievement %']];
    const data = aggregateData.loGroups.map(group => [
      group.lo,
      group.totalMarks,
      aggregateData.loTotals[group.lo].total,
      aggregateData.loTotals[group.lo].maxPossible,
      `${aggregateData.loTotals[group.lo].percentage}%`
    ]);

    doc.autoTable({
      startY: 42,
      head: headers,
      body: data,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [30, 58, 110], textColor: 255, fontStyle: 'bold' }
    });

    // Cluster-wise breakdown
    let yPos = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Cluster-wise Breakdown', 14, yPos);
    
    const clusterHeaders = [['Cluster', 'Schools', 'Students', ...aggregateData.loGroups.map(g => g.lo)]];
    const clusterData = aggregateData.clusters.map(cluster => [
      cluster,
      aggregateData.clusterTotals[cluster].schools,
      aggregateData.clusterTotals[cluster].students,
      ...aggregateData.loGroups.map(g => `${aggregateData.clusterTotals[cluster].loTotals[g.lo].percentage}%`)
    ]);

    doc.autoTable({
      startY: yPos + 5,
      head: clusterHeaders,
      body: clusterData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: 'bold' }
    });

    doc.save(`Block_Report_${aggregateData.config.class_name}_${aggregateData.config.subject}.pdf`);
    toast.success('PDF exported!');
  };

  const handleExportExcel = () => {
    if (!aggregateData) return;

    const wsData = [
      ['BLOCK AGGREGATE REPORT'],
      [],
      ['Block:', aggregateData.block, 'District:', aggregateData.district],
      ['Class:', aggregateData.config.class_name, 'Subject:', aggregateData.config.subject, 'Exam:', aggregateData.config.exam],
      ['Total Clusters:', aggregateData.clusters.length, 'Total Schools:', aggregateData.totalSchools, 'Total Students:', aggregateData.totalStudents],
      [],
      ['BLOCK SUMMARY'],
      ['LO Code', 'Max Marks', 'Total Obtained', 'Max Possible', 'Achievement %']
    ];

    aggregateData.loGroups.forEach(group => {
      wsData.push([
        group.lo,
        group.totalMarks,
        aggregateData.loTotals[group.lo].total,
        aggregateData.loTotals[group.lo].maxPossible,
        `${aggregateData.loTotals[group.lo].percentage}%`
      ]);
    });

    wsData.push([]);
    wsData.push(['CLUSTER-WISE BREAKDOWN']);
    wsData.push(['Cluster', 'Schools', 'Students', ...aggregateData.loGroups.map(g => g.lo)]);

    aggregateData.clusters.forEach(cluster => {
      wsData.push([
        cluster,
        aggregateData.clusterTotals[cluster].schools,
        aggregateData.clusterTotals[cluster].students,
        ...aggregateData.loGroups.map(g => `${aggregateData.clusterTotals[cluster].loTotals[g.lo].percentage}%`)
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Block Report');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Block_Report_${aggregateData.config.class_name}_${aggregateData.config.subject}.xlsx`);
    
    toast.success('Excel exported!');
  };

  const handleWhatsAppShare = () => {
    if (!aggregateData) return;
    
    let message = `📊 *BLOCK AGGREGATE REPORT*\n\n`;
    message += `📍 Block: ${aggregateData.block}\n`;
    message += `📍 District: ${aggregateData.district}\n`;
    message += `📚 Class: ${aggregateData.config.class_name} | Subject: ${aggregateData.config.subject}\n`;
    message += `📝 Exam: ${aggregateData.config.exam} ${aggregateData.config.year}\n`;
    message += `🏫 Clusters: ${aggregateData.clusters.length} | Schools: ${aggregateData.totalSchools} | Students: ${aggregateData.totalStudents}\n\n`;
    message += `*Block LO Achievement:*\n`;
    
    aggregateData.loGroups.forEach(group => {
      const pct = aggregateData.loTotals[group.lo].percentage;
      const emoji = pct >= 70 ? '✅' : pct >= 50 ? '🟡' : '🔴';
      message += `${emoji} ${group.lo}: ${pct}%\n`;
    });
    
    message += `\n_Generated by Teacher Utility Toolkit_`;
    
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-navy">Block Report</h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">Aggregate LO-wise report for your block</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchEntries} size="sm">
            <RefreshCw className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="outline" onClick={handleExportPDF} disabled={!aggregateData} size="sm">
            <FileText className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">PDF</span>
          </Button>
          <Button variant="outline" onClick={handleExportExcel} disabled={!aggregateData} size="sm">
            <Download className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Excel</span>
          </Button>
          <Button onClick={handleWhatsAppShare} disabled={!aggregateData} className="bg-green-600 hover:bg-green-700" size="sm">
            <MessageCircle className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Share</span>
          </Button>
        </div>
      </div>

      {/* Config selector */}
      {uniqueConfigs.length > 0 && (
        <Card className="mb-4 md:mb-6">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <span className="text-sm font-medium whitespace-nowrap">Select Report:</span>
              <select 
                className="flex-1 px-3 py-2 border rounded-md text-sm"
                value={`${selectedConfig.class_name}-${selectedConfig.subject}-${selectedConfig.exam}-${selectedConfig.year}`}
                onChange={(e) => {
                  const config = uniqueConfigs.find(c => c.key === e.target.value);
                  if (config) setSelectedConfig(config);
                }}
              >
                {uniqueConfigs.map(config => (
                  <option key={config.key} value={config.key}>
                    Class {config.class_name} - {config.subject} ({config.exam} {config.year})
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      {aggregateData ? (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 mb-4 md:mb-6">
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold text-navy">{aggregateData.clusters.length}</div>
                <div className="text-xs md:text-sm text-slate-500">Clusters</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold text-navy">{aggregateData.totalSchools}</div>
                <div className="text-xs md:text-sm text-slate-500">Schools</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold text-navy">{aggregateData.totalStudents}</div>
                <div className="text-xs md:text-sm text-slate-500">Students</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold text-navy">{aggregateData.loGroups.length}</div>
                <div className="text-xs md:text-sm text-slate-500">LO Codes</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold text-emerald-600">
                  {(() => {
                    const totalPct = aggregateData.loGroups.reduce((sum, g) => sum + parseFloat(aggregateData.loTotals[g.lo].percentage), 0);
                    return (totalPct / aggregateData.loGroups.length).toFixed(1);
                  })()}%
                </div>
                <div className="text-xs md:text-sm text-slate-500">Avg Achievement</div>
              </CardContent>
            </Card>
          </div>

          {/* Block Summary */}
          <Card className="mb-4 md:mb-6">
            <CardHeader className="p-3 md:p-6">
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <Building2 className="w-4 h-4 md:w-5 md:h-5" />
                Block LO Achievement
              </CardTitle>
              <p className="text-xs md:text-sm text-slate-500 mt-1">
                Block: {aggregateData.block} | District: {aggregateData.district}
              </p>
            </CardHeader>
            <CardContent className="p-0 md:p-6 md:pt-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm border-collapse">
                  <thead>
                    <tr className="bg-navy text-white">
                      <th className="px-2 md:px-4 py-2 text-left">LO Code</th>
                      <th className="px-2 md:px-4 py-2 text-center">Max Marks</th>
                      <th className="px-2 md:px-4 py-2 text-center">Total Obtained</th>
                      <th className="px-2 md:px-4 py-2 text-center">Max Possible</th>
                      <th className="px-2 md:px-4 py-2 text-center">Achievement %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregateData.loGroups.map((group, idx) => {
                      const pct = parseFloat(aggregateData.loTotals[group.lo].percentage);
                      const bgColor = pct >= 70 ? 'bg-emerald-50' : pct >= 50 ? 'bg-amber-50' : 'bg-red-50';
                      const textColor = pct >= 70 ? 'text-emerald-700' : pct >= 50 ? 'text-amber-700' : 'text-red-700';
                      
                      return (
                        <tr key={group.lo} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-2 md:px-4 py-2 font-mono font-medium">{group.lo}</td>
                          <td className="px-2 md:px-4 py-2 text-center">{group.totalMarks}</td>
                          <td className="px-2 md:px-4 py-2 text-center font-medium">{aggregateData.loTotals[group.lo].total}</td>
                          <td className="px-2 md:px-4 py-2 text-center">{aggregateData.loTotals[group.lo].maxPossible}</td>
                          <td className={`px-2 md:px-4 py-2 text-center font-bold ${bgColor} ${textColor}`}>
                            {aggregateData.loTotals[group.lo].percentage}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Cluster Breakdown */}
          <Card>
            <CardHeader className="p-3 md:p-6">
              <CardTitle className="text-base md:text-lg">Cluster-wise Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="p-0 md:p-6 md:pt-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm border-collapse">
                  <thead>
                    <tr className="bg-amber text-white">
                      <th className="px-2 md:px-4 py-2 text-left">Cluster</th>
                      <th className="px-2 md:px-4 py-2 text-center">Schools</th>
                      <th className="px-2 md:px-4 py-2 text-center">Students</th>
                      {aggregateData.loGroups.map(g => (
                        <th key={g.lo} className="px-2 md:px-4 py-2 text-center">{g.lo}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {aggregateData.clusters.map((cluster, idx) => (
                      <tr key={cluster} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-2 md:px-4 py-2 font-medium">{cluster}</td>
                        <td className="px-2 md:px-4 py-2 text-center">{aggregateData.clusterTotals[cluster].schools}</td>
                        <td className="px-2 md:px-4 py-2 text-center">{aggregateData.clusterTotals[cluster].students}</td>
                        {aggregateData.loGroups.map(g => {
                          const pct = parseFloat(aggregateData.clusterTotals[cluster].loTotals[g.lo].percentage);
                          const textColor = pct >= 70 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600';
                          return (
                            <td key={g.lo} className={`px-2 md:px-4 py-2 text-center font-medium ${textColor}`}>
                              {pct}%
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-8 md:p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-700 mb-2">No Data Available</h3>
            <p className="text-sm text-slate-500">
              Add marks data to generate block aggregate reports.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
